import types from '../mutation-types';
import TicketsAPI from '../../api/tickets';

export const state = {
  records: [],
  notes: {},
  activities: {},
  meta: { count: 0, currentPage: 1 },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingNotes: false,
    isCreatingNote: false,
    isDeletingNote: false,
    isFetchingActivities: false,
  },
};

export const getters = {
  getTickets: _state => _state.records,
  getTicketById: _state => id =>
    _state.records.find(t => String(t.id) === String(id)),
  getNotesByTicketId: _state => ticketId => _state.notes[ticketId] || [],
  getActivitiesByTicketId: _state => ticketId => _state.activities[ticketId] || [],
  getUIFlags: _state => _state.uiFlags,
  getMeta: _state => _state.meta,
};

export const actions = {
  async get({ commit }, params = {}) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await TicketsAPI.get(params);
      commit(types.SET_TICKETS, data.data.payload);
      commit(types.SET_TICKETS_META, data.data.meta);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetching: false });
    }
  },

  async show({ commit }, id) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await TicketsAPI.show(id);
      commit(types.SET_TICKET, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetching: false });
    }
  },

  async create({ commit }, ticketData) {
    commit(types.SET_TICKETS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await TicketsAPI.create(ticketData);
      commit(types.ADD_TICKET, data);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isCreating: false });
    }
  },

  async update({ commit }, { id, ...ticketData }) {
    commit(types.SET_TICKETS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await TicketsAPI.update(id, ticketData);
      commit(types.EDIT_TICKET, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit(types.SET_TICKETS_UI_FLAG, { isDeleting: true });
    try {
      await TicketsAPI.delete(id);
      commit(types.DELETE_TICKET, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isDeleting: false });
    }
  },

  async bulkActions(_, data) {
    await TicketsAPI.bulkActions(data);
  },

  async getNotes({ commit }, ticketId) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetchingNotes: true });
    try {
      const { data } = await TicketsAPI.getNotes(ticketId);
      commit(types.SET_TICKET_NOTES, { ticketId, notes: data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetchingNotes: false });
    }
  },

  async createNote({ commit }, { ticketId, files, ...noteData }) {
    commit(types.SET_TICKETS_UI_FLAG, { isCreatingNote: true });
    try {
      const { data } = await TicketsAPI.createNote(ticketId, { ...noteData, files });
      commit(types.ADD_TICKET_NOTE, { ticketId, note: data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isCreatingNote: false });
    }
  },

  async getActivities({ commit }, ticketId) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetchingActivities: true });
    try {
      const { data } = await TicketsAPI.getActivities(ticketId);
      commit(types.SET_TICKET_ACTIVITIES, { ticketId, activities: data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetchingActivities: false });
    }
  },

  async updateNote({ commit }, { ticketId, noteId, newFiles, removeAttachments, ...noteData }) {
    try {
      const { data } = await TicketsAPI.updateNote(ticketId, noteId, {
        ...noteData,
        newFiles,
        removeAttachments,
      });
      commit(types.UPDATE_TICKET_NOTE, { ticketId, note: data });
    } catch (error) {
      throw new Error(error);
    }
  },

  async uploadDescriptionFiles({ commit }, { ticketId, files }) {
    try {
      const { data } = await TicketsAPI.uploadDescriptionFiles(ticketId, files);
      commit(types.EDIT_TICKET, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async removeDescriptionFile({ commit }, { ticketId, fileId }) {
    try {
      const { data } = await TicketsAPI.removeDescriptionFile(ticketId, fileId);
      commit(types.EDIT_TICKET, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async deleteNote({ commit }, { ticketId, noteId }) {
    commit(types.SET_TICKETS_UI_FLAG, { isDeletingNote: true });
    try {
      await TicketsAPI.deleteNote(ticketId, noteId);
      commit(types.DELETE_TICKET_NOTE, { ticketId, noteId });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isDeletingNote: false });
    }
  },
};

export const mutations = {
  [types.SET_TICKETS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_TICKETS](_state, records) {
    _state.records = records;
  },
  [types.SET_TICKET](_state, ticket) {
    const index = _state.records.findIndex(t => t.id === ticket.id);
    if (index !== -1) {
      _state.records[index] = ticket;
    } else {
      _state.records.push(ticket);
    }
  },
  [types.ADD_TICKET](_state, ticket) {
    _state.records.unshift(ticket);
  },
  [types.EDIT_TICKET](_state, ticket) {
    const index = _state.records.findIndex(t => t.id === ticket.id);
    if (index !== -1) _state.records[index] = ticket;
  },
  [types.DELETE_TICKET](_state, id) {
    _state.records = _state.records.filter(t => String(t.id) !== String(id));
  },
  [types.SET_TICKETS_META](_state, meta) {
    _state.meta = meta;
  },
  [types.SET_TICKET_NOTES](_state, { ticketId, notes }) {
    _state.notes = { ..._state.notes, [ticketId]: notes };
  },
  [types.ADD_TICKET_NOTE](_state, { ticketId, note }) {
    const existing = _state.notes[ticketId] || [];
    _state.notes = { ..._state.notes, [ticketId]: [...existing, note] };
  },
  [types.UPDATE_TICKET_NOTE](_state, { ticketId, note }) {
    const existing = _state.notes[ticketId] || [];
    _state.notes = {
      ..._state.notes,
      [ticketId]: existing.map(n => (n.id === note.id ? note : n)),
    };
  },
  [types.DELETE_TICKET_NOTE](_state, { ticketId, noteId }) {
    const existing = _state.notes[ticketId] || [];
    _state.notes = {
      ..._state.notes,
      [ticketId]: existing.filter(n => n.id !== noteId),
    };
  },
  [types.SET_TICKET_ACTIVITIES](_state, { ticketId, activities }) {
    _state.activities = { ..._state.activities, [ticketId]: activities };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
